// utils.js
import _ceil from 'lodash/ceil';
import _round from 'lodash/round';

import { calculateDobPerLocation, formulateChartUrl, formulateGenderFromUrlFragment } from './intermediary_url_service';
import {
  calculateAgeInMonthsWithTwoDates,
  calculateZScore,
  convertInchesToCms,
  parseUrlForSimpleChartType,
} from './lm_utilities';
import { getAgeDesc, getLifeStage } from './lm_title_description_service';
import {
  getXAxisPointsFor0To24Months,
  getXAxisPointsFor21To100Years,
  getXAxisPointsFor2To20Years,
} from './data_points_service';
import { plural } from 'pluralize';
import { parseUrlForGenderWithString } from './maps';

export const initialUnit = (type) => {
  let unit = 'cms';
  if (type === 'weight') unit = 'lbs';
  if (type === 'bmi') unit = 'cms-kgs';

  return unit;
};
export const setInitialState = (pathname, localStorageData, type) => {
  const path = pathname;
  const parts = path.split('/'); // get gender form url
  const gender = parts[2];
  let freshForm = {
    gender: formulateGenderFromUrlFragment(gender),
    dob: calculateDobPerLocation(pathname).toLocaleDateString('en-CA'),
    md: new Date().toLocaleDateString('en-CA'),
    unit: initialUnit(type),
    in_kgs: 0,
    in_lbs: 0,
    in_cms: 0,
    in_incs: 0,
    l_measurement: type === 'weight' ? 25.1 : 75.5,
    l_height_ft: 0,
    l_height_in: 0,
    member_id: null,
  };

  if (type === 'bmi') {
    freshForm.w_measurement = 11.2;
  }

  let tempForm = localStorageData ? JSON.parse(localStorageData) : freshForm;
  let temp_age = _ceil(calculateAgeInMonthsWithTwoDates(tempForm.dob, tempForm.md));
  let tempUrl = formulateChartUrl(tempForm, `${type}-charts`, temp_age);

  if (tempUrl === pathname) return tempForm;
  else {
    freshForm.gender = tempForm ? tempForm.gender : freshForm.gender;
    freshForm.dob = tempForm ? tempForm.dob : freshForm.dob;
    freshForm.member_id = tempForm ? tempForm.member_id : freshForm.member_id;
    return freshForm;
  }
};

export const getBlurbData = (unit, data, pathname) => {
  let formatted = data;
  if (data) {
    if (unit === 'inches')
      formatted = {
        percentile_3: _round(parseFloat(_round(data.p_3, 2) * 0.393701), 1),
        percentile_97: _round(parseFloat(_round(data.p_97 || data.p_95, 2) * 0.393701), 1),
        percentile_2: _round(parseFloat(_round(data.p_2, 2) * 0.393701), 1),
        percentile_98: _round(parseFloat(_round(data.p_98, 2) * 0.393701), 1),
        percentile_50: _round(parseFloat(_round(data.p_50, 2) * 0.393701), 1),
        ageDesc: pathname ? getAgeDesc(pathname) : getAgeDesc(window.location.pathname),
      };
    else if (unit === 'lbs')
      formatted = {
        percentile_3: _round(parseFloat(_round(data.p_3, 2) * 2.20462), 1),
        percentile_97: _round(parseFloat(_round(data.p_97 || data.p_95, 2) * 2.20462), 1),
        percentile_2: _round(parseFloat(_round(data.p_2, 2) * 2.20462), 1),
        percentile_98: _round(parseFloat(_round(data.p_98, 2) * 2.20462), 1),
        percentile_50: _round(parseFloat(_round(data.p_50, 2) * 2.20462), 1),
        ageDesc: pathname ? getAgeDesc(pathname) : getAgeDesc(window.location.pathname),
      };
    else
      formatted = {
        percentile_3: _round(data.p_3, 2),
        percentile_97: _round(data.p_97 || data.p_95, 2),
        percentile_2: _round(data.p_2, 2),
        percentile_98: _round(data.p_98, 2),
        percentile_50: _round(data.p_50, 2),
        ageDesc: pathname ? getAgeDesc(pathname) : getAgeDesc(window.location.pathname),
      };
  } else {
    formatted = {
      percentile_3: '',
      percentile_97: '',
      percentile_2: '',
      percentile_98: '',
      percentile_50: '',
    };
  }

  return formatted;
};

// export const getBlurbData = (unit, data, pathname) => {
//   if (!data) {
//     return {
//       percentile_3: '',
//       percentile_97: '',
//       percentile_2: '',
//       percentile_98: '',
//       percentile_50: '',
//     };
//   }

//   const factor = unit === 'inches' ? 0.393701 : unit === 'lbs' ? 2.20462 : null;

//   const convert = (val) => (typeof val === 'number' ? (factor ? _round(val * factor, 1) : _round(val, 2)) : '');

//   return {
//     percentile_3: convert(data.p_3),
//     percentile_97: convert(data.p_97 ?? data.p_95), // <--- Use nullish coalescing directly
//     percentile_2: convert(data.p_2),
//     percentile_98: convert(data.p_98),
//     percentile_50: convert(data.p_50),
//     ageDesc: getAgeDesc(pathname || window.location.pathname),
//   };
// };

export const getAxisPoints = (options, calculated_age) => {
  if (calculated_age <= 24 || (calculated_age > 24 && calculated_age < 25)) {
    options.xAxisPoints = getXAxisPointsFor0To24Months();
    options.xAxistickInterval = 2;
    options.yAxistickInterval = 2;
    options.plotBandWidth = 1;
  } else if (calculated_age >= 288 && calculated_age <= 1200) {
    options.xAxisPoints = getXAxisPointsFor21To100Years();
    options.xAxistickInterval = 2;
    options.yAxistickInterval = 2;
    options.plotBandWidth = 1;
  } else {
    options.xAxisPoints = getXAxisPointsFor2To20Years();
    options.xAxistickInterval = 1;
    options.yAxistickInterval = 20;
    options.plotBandWidth = 5;
  }

  return options;
};

export const calculatePercentileWithZscores = (floorData, ceilData, calculated_age, floor, ceil, value) => {
  const box_cox = floorData.charts[0].box_cox;
  const median = floorData.charts[0].median;
  const coff = floorData.charts[0].coefficient;
  const zScore1 = calculateZScore(value, box_cox, median, coff);

  const box_cox_ceil = ceilData.charts[0].box_cox;
  const median_ceil = ceilData.charts[0].median;
  const coff_ceil = ceilData.charts[0].coefficient;
  const zScore2 = calculateZScore(value, box_cox_ceil, median_ceil, coff_ceil);

  return ((calculated_age - floor) * (zScore2 - zScore1)) / (ceil - floor) + zScore1;
};
// export const calculatePercentileWithZscores = (floorData, ceilData, calculated_age, floor, ceil, value) => {
//   const roundedValue = Math.round(value * 1000) / 1000;
//   const box_cox = floorData.charts[0].box_cox;
//   const median = floorData.charts[0].median;
//   const roundedMedian = Math.round(median * 1000) / 1000;
//   const coff = floorData.charts[0].coefficient;
//   const zScore1 = calculateZScore(roundedValue, box_cox, roundedMedian, coff);

//   const box_cox_ceil = ceilData.charts[0].box_cox;
//   const median_ceil = ceilData.charts[0].median;
//   const roundedMedian_ceil = Math.round(median_ceil * 1000) / 1000;
//   const coff_ceil = ceilData.charts[0].coefficient;
//   const zScore2 = calculateZScore(roundedValue, box_cox_ceil, roundedMedian_ceil, coff_ceil);
//   console.log('value:', value, 'median:', median, 'equals?', roundedValue === median);
//   console.log('diff:', Math.abs(value - median));
//   return ((calculated_age - floor) * (zScore2 - zScore1)) / (ceil - floor) + zScore1;
// };
export const getValueInCms = (unit, value) => {
  if (unit === 'inches') return convertInchesToCms(value);
};

export const concantQuery = (obj) => {
  if (typeof obj !== 'object' || obj === null) return '';

  const query = Object.keys(obj).reduce((_query, key) => {
    // Explicitly check for undefined and null
    if (obj[key] !== undefined && obj[key] !== null) {
      return _query + `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}&`;
    }
    return _query;
  }, '');

  return query.length > 0 ? '?' + query.replace(/&$/, '') : query;
};
export const base64ToBlob = (base64, mimeType) => {
  const byteCharacters = atob(base64.split(',')[1]);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

const splitUrlToParts = (pathname) => {
  if (!pathname) return [];

  return pathname.split('/').filter(Boolean);
};
// export const getFileNameServer = (blurbData, hashArray, url) => {
//   try {
//     const urlParts = splitUrlToParts(url);

//     let fileName =
//       hashArray[3] +
//       ' ' +
//       plural(getLifeStage(url)) +
//       ' ' +
//       parseUrlForSimpleChartType(urlParts[0]) +
//       ' ' +
//       parseUrlForGenderWithString(hashArray) +
//       ' chart';

//     fileName = fileName.toLowerCase().replaceAll(' ', '-');

//     return fileName;
//   } catch (e) {
//     console.error('Error generating fileName:', e);
//     return 'default-chart';
//   }
// };
export const getFormattedUTCDate = (date) => {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
};
export const getFileNameServer = (blurbData, hashArray, url, date = null) => {
  const splitParts = splitUrlToParts(url);

  const baseFileName =
    hashArray[3] +
    ' ' +
    plural(getLifeStage(url)) +
    ' ' +
    parseUrlForSimpleChartType(splitParts[0]) +
    ' ' +
    parseUrlForGenderWithString(hashArray) +
    ' chart';

  const cleanFileName = baseFileName.toLowerCase().replaceAll(' ', '-');

  if (date) {
    const formattedDate = getFormattedUTCDate(date);
    return `${cleanFileName}-${formattedDate}`;
  }

  return cleanFileName;
};
// export const getChartImageAltValueServer = (blurbData, hashArray, url) => {
//   try {
//     const urlParts = splitUrlToParts(url);

//     let fileName =
//       blurbData?.ageDesc +
//       ' ' +
//       plural(getLifeStage(url)) +
//       ' ' +
//       parseUrlForSimpleChartType(urlParts[0]) +
//       ' ' +
//       parseUrlForGenderWithString(hashArray) +
//       ' chart';

//     fileName = fileName.toLowerCase();
//     return fileName;
//   } catch (e) {
//     console.error('Error generating chartImageAltValue:', e);
//     return 'chart'; // optional fallback
//   }
// };
export const getChartImageAltValueServer = (blurbData, hashArray, url, date = null) => {
  const splitParts = splitUrlToParts(url); // already safe
  const baseFileName =
    hashArray[3] +
    ' ' +
    plural(getLifeStage(url)) +
    ' ' +
    parseUrlForSimpleChartType(splitParts[0]) +
    ' ' +
    parseUrlForGenderWithString(hashArray) +
    ' chart';

  const cleanFileName = baseFileName.toLowerCase().replaceAll(' ', '-');

  if (date) {
    // Add date in yyyymmdd format
    const formattedDate = getFormattedUTCDate(date);
    return `${cleanFileName}-${formattedDate}`;
  }

  return cleanFileName;
};

// export const checkImageExistsServer = async (fileName) => {
//   const imageUrl = process.env.NEXT_PUBLIC_CHART_IMAGE_URL + fileName + '.png';

//   try {
//     const response = await fetch(imageUrl, { method: 'HEAD' });
//     if (response.ok) {
//       return imageUrl;
//     } else {
//       return null;
//     }
//   } catch (error) {
//     return null;
//   }
// };
export const checkImageExistsServer = async (fileName) => {
  const transformation = 'c_thumb,g_face,q_auto,f_auto,h_150,w_150';

  const baseUrl = process.env.NEXT_PUBLIC_CHART_IMAGE_URL; // should include /upload/
  const [beforeUpload, afterUpload] = baseUrl.split('/upload');

  const imageUrl = `${beforeUpload}/upload/${transformation}/${afterUpload}/${fileName}.png`;

  try {
    const response = await fetch(imageUrl, { method: 'HEAD' });
    if (response.ok) {
      return imageUrl;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

export const getAgeFloorAndCeil = (age) => {
  if (age <= 24) {
    return [String(age), String(age)];
  } else if (age <= 240) {
    const floor = String(age - 0.5);
    const ceil = age === 240 ? String(age) : String(age + 0.5);
    return [floor, ceil];
  } else if (age > 240) {
    let newValue = age;
    const remainder = age % 12;
    if (remainder > 0) newValue -= remainder;
    return [String(newValue - 12), String(newValue)];
  }

  // return [_floor(age) - 12, _ceil(age) + 12];
};

export function formatAgeDescription(ageDesc) {
  if (!ageDesc) return '';

  return ageDesc
    .replace(/years?/g, 'year')
    .replace(/months?/g, 'month')
    .replace(/weeks?/g, 'week');
}
